import { makeAutoObservable, runInAction, flow } from 'mobx';
import { JwtPayload } from 'jsonwebtoken';
import { UserApi, IUserApi } from './UserApi';
import { RootStore } from './RootStore';
import { ROLE_CLAIM_IDENTIFIER } from '../constants';
import { filterObjectNull, displayErrorMessage } from '../helpers/utils';
import { toast } from 'react-toastify';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  locationId: string;
  userName: string;
  preferredCurrency: string;
}

export interface Location {
  id: number;
  name: string;
  currency: string;
}

export class UserStore {
  locations: Location[] = [];
  currencies: string[] = [];
  error: string | null = null;
  userProfile: UserProfile = {} as UserProfile;
  isAdmin: boolean | null = null;
  currency: string | null = null;
  isUserAuthorized = false;
  status: string | undefined;

  private api: IUserApi;

  constructor(
    private rootStore: RootStore,
    api?: IUserApi
  ) {
    this.api = api || new UserApi();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  clearError() {
    runInAction(() => {
      this.error = null;
    });
  }

  @flow.bound
  *updateUserProfile(data: Partial<UserProfile>) {
    this.clearError();
    try {
      const updatedProfile: UserProfile = yield this.api.updateUserProfile(data);
      runInAction(() => {
        this.userProfile = { ...this.userProfile, ...filterObjectNull(updatedProfile) };
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Unknown error';
      });
      displayErrorMessage(error);
    }
  }

  @flow.bound
  *getUserProfile(): Generator<Promise<UserProfile>, void, UserProfile> {
    this.clearError();
    try {
      const profile: UserProfile = yield this.api.getUserProfile();
      runInAction(() => {
        this.userProfile = { ...this.userProfile, ...filterObjectNull(profile) };
        this.currency = profile.preferredCurrency;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Unknown error';
      });
      displayErrorMessage(error);
      this.rootStore.authStore.logoutUser();
    }
  }

  @flow.bound
  *loginUserWinAuth() {
    this.clearError();
    try {
      yield Promise.all([this.getUserProfile()]);
      runInAction(() => {
        this.isUserAuthorized = true;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Login failed';
        this.isUserAuthorized = false;
      });
      displayErrorMessage(error);
    }
  }

  async logoutUserWinAuth() {
    this.clearError();
    try {
      runInAction(() => this.rootStore.spinnerStore.showSpinner());
      runInAction(() => {
        this.userProfile = {} as UserProfile;
        this.isAdmin = null;
        this.isUserAuthorized = false;
        this.rootStore.authStore.logoutUser();
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Logout failed';
      });
      displayErrorMessage(error);
    } finally {
      runInAction(() => this.rootStore.spinnerStore.hideSpinner());
    }
  }

  async contactUs(data: { name: string; email: string; message: string }) {
    this.clearError();
    try {
      await this.api.contactUs(data);
      toast.success('Your message was sent. Thank you for contacting us.');
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'Failed to send message';
      });
      displayErrorMessage(error);
    }
  }

  async getLocations() {
    try {
      const locs: Location[] = await this.api.getLocations();
      runInAction(() => {
        this.locations = locs;
      });
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
        this.error = error.message;
      });
      displayErrorMessage(error);
    }
  }

  async getCurrencies() {
    if (this.currencies.length > 0) return;
    try {
      const currs: string[] = await this.api.getCurrencies();
      runInAction(() => {
        this.currencies = currs;
      });
    } catch (error: any) {
      runInAction(() => {
        this.status = 'error';
        this.error = error.message;
      });
      displayErrorMessage(error);
    }
  }

  setAdminIndicator(tokenData: { payload: JwtPayload }) {
    runInAction(() => {
      this.isAdmin = tokenData.payload[ROLE_CLAIM_IDENTIFIER] === 'Admin';
    });
  }

  setCurrency(data: string) {
    runInAction(() => {
      this.currency = data;
    });
  }
}
